import type { BoPostingState, BoPostingStateDetail } from '@/types';

/**
 * LR-712: the one place a BackOffice posting is put into words.
 *
 * Every operator-facing sentence about a stalled posting is derived HERE, from the
 * posting's current state — never from `Position.last_bo_error`, the single
 * free-text column that used to carry it. That column is one string for a whole
 * trade, overwritten by whichever posting failed last, so it could not say which
 * posting it described and routinely described a different one: TRD-111 showed
 * "account not found" (a rejected collateral hold, days earlier) while the trade
 * was actually stuck on an opening transfer that had timed out. The reader has no
 * way to tell those apart, and the wrong one decides the wrong remedy — "fix the
 * account and retry" on a posting BackOffice may already hold is how a trade gets
 * booked twice.
 *
 * The states differ in exactly one fact that matters to a person: whether
 * BackOffice may already hold the booking, or is known to. So they never share a
 * sentence, and no caller composes its own — there were two dictionaries for these
 * four states before this file (the row tooltip's and the trade page's), which is
 * one more than the number of true answers.
 *
 * Two registers, same facts:
 *   `boPostingHeadline` — one line, for a tooltip or a confirm dialog.
 *   `boPostingCopy`     — title + explanation + tone, for the trade page panel.
 */

/**
 * The states a re-POST can settle: its own answer (409 or 200) is the only thing
 * that decides them, which is why the action exists for these two and for no
 * other. Named once because three separate surfaces read it — the button, the
 * body copy and the line that replaces the button — and LR-711 round 2 taught us
 * what happens when they are gated independently: two of the three learned
 * `confirmed_unrecorded` and the third did not.
 */
export const RECONCILABLE_STATES: ReadonlyArray<BoPostingStateDetail['state']> = [
  'timed_out',
  'confirmed_unrecorded',
];

/**
 * LR-706 (g): the words for the action that closes a LEGACY posting — the one
 * BackOffice can never be asked to settle, because it carries no request id.
 *
 * The button names the direction, and the direction is the whole point: every
 * other action in this panel SENDS something to BackOffice, and this one reads.
 * Nothing here may read as "post it again" — on a legacy posting that is exactly
 * the double booking the guard has been holding the trade to prevent.
 */
export const RECORD_FROM_BO_COPY = {
  action: 'Record from BackOffice',
  hint:
    'Reads what BackOffice holds for this posting and writes our side from it. ' +
    'Nothing is posted to BackOffice — a posting this old cannot be sent again.',
  dialogTitle: 'Record what BackOffice holds',
  /** Above the table of transactions. */
  dialogLead:
    'These are the transactions BackOffice holds for this posting. Recording them ' +
    'writes our side from BackOffice’s own figures and settles the leg they ' +
    'belong to — a trade whose other leg is still stuck stays where it is until ' +
    'that one is recorded too.',
  dialogEffect:
    'No posting is sent. If BackOffice’s amount differs from ours, BackOffice’s is ' +
    'what gets recorded — check the two columns before confirming.',
  loading: 'Asking BackOffice what it holds…',
  emptyFallback:
    'BackOffice could not answer for this posting. Nothing was written.',
} as const;

/**
 * LR-715: the words for the one action that closes a posting BackOffice
 * contradicted itself about.
 *
 * Not state-dependent like `boPostingCopy` — it is offered in exactly one state —
 * but it lives here for the reason this module exists: it must not contradict the
 * panel text above it. The button says what the operator has ALREADY done ("close
 * after reconciling"), never what the click does to BackOffice, because it does
 * nothing there: it closes our record of a problem the person fixed by hand.
 */
export const SETTLE_MANUALLY_COPY = {
  action: 'Close — reconciled in BackOffice',
  /** Beside the button, in the panel. */
  hint:
    'Only after you have checked BackOffice and fixed the duplicate there. This ' +
    'closes our record of the problem; it changes nothing in BackOffice.',
  dialogTitle: 'Close this posting by hand',
  /** Follows "Position 42 (AAPL) ". */
  dialogLead:
    'has a BackOffice posting that BackOffice may hold twice: it booked our ' +
    'request id a second time instead of refusing the repeat',
  dialogEffect:
    'Closing it records that you reconciled the books in BackOffice yourself. ' +
    'Nothing is posted, cancelled or amended there — this only stops the trade ' +
    'reporting a problem you have already fixed, and cannot be undone from here.',
  reasonLabel: 'What did you reconcile in BackOffice?',
  reasonPlaceholder: 'e.g. cancelled the duplicate transfer TR-8842 in BackOffice',
} as const;

/**
 * Mirrors the backend's OP_LABELS (apps/positions/services/bo_idempotency.py) so
 * the 409 an operator reads and the panel they read it on name the same posting
 * the same way. Lowercase: every use here sits mid-sentence.
 */
export const OP_LABELS: Record<string, string> = {
  hold: 'hold',
  release: 'hold release',
  transfer_open: 'opening transfer',
  transfer_close: 'closing transfer',
  transfer_recall: 'recall transfer',
  transfer_mtm: 'mark-to-market transfer',
};

/** Why a `timed_out` posting has no answer — the honest half of "we don't know". */
export const REASON_LABELS: Record<string, string> = {
  read_timeout: 'the reply never arrived',
  unreadable_2xx: 'BackOffice said yes in a form we could not read',
  worker_died: 'the worker stopped mid-posting',
};

/**
 * What the posting IS: what it does and which leg it moves — "opening transfer
 * (securities leg)". An `op` the frontend does not know degrades to its own name
 * rather than to a wrong one, the same way the backend's twin does.
 *
 * Plain strings rather than `BoPostingState`'s narrower leg union: the read-back
 * answer (LR-706 (g)) carries the guard block's own `leg`, which is a free field on
 * the model — `securities_close` and `securities_recall` live there too — and this
 * label is the one place both shapes need naming the same way.
 */
export function postingLabel(posting: { op: string; leg: string }): string {
  const what = OP_LABELS[posting.op] ?? posting.op.replace(/_/g, ' ');
  return `${what} (${posting.leg} leg)`;
}

function since(posting: Pick<BoPostingState, 'attempted_at'>): string {
  return posting.attempted_at ? ` since ${posting.attempted_at.slice(0, 10)}` : '';
}

/**
 * One line: what is stuck, what BackOffice did about it, and what happens next.
 *
 * For a row tooltip and for a confirm dialog — places with no room to explain, and
 * where the reader is deciding whether to act at all. Takes the LIST shape, so it
 * works from a blotter row without fetching the trade.
 */
export function boPostingHeadline(posting: BoPostingState): string {
  const what = postingLabel(posting);
  const when = since(posting);
  switch (posting.state) {
    case 'rejected':
      return `BackOffice refused the ${what}${when} — nothing was booked; fix the trade and post again`;
    case 'failed':
      return `The ${what} did not reach BackOffice${when} — nothing was booked; it will be retried`;
    case 'confirmed_unrecorded':
      // Not "may hold it": we know it does. Saying `may` here would understate
      // what BackOffice already told us, and this is the one row where the two
      // books are known to disagree.
      //
      // It used to end "until it is reconciled", and LR-715 took that clause out.
      // This state covers two situations a ROW cannot tell apart — the re-post is
      // still awaited, or BackOffice answered it by booking the posting a second
      // time — and in the second one reconciling is the one thing that must not
      // happen: BackOffice has just proved it will not refuse the repeat, so
      // another send is another duplicate. The fact that separates them
      // (`is_contradicted`) is on the detail payload only, deliberately: it costs
      // a query the blotter would pay per row, for an action the blotter does not
      // offer. So the headline states what is true either way and sends the reader
      // to the page where the difference — and the remedy — exists.
      return `BackOffice booked the ${what}${when}, but this trade never recorded it — the books disagree; open the trade to see what closes it`;
    case 'timed_out':
      return `The ${what} never confirmed${when} — BackOffice may already hold it, so it is blocked until it is reconciled`;
    default: {
      // The same compile-time guard the panel and the dialog carry (LR-711
      // round 4), and the reason LR-712's merge of the three dictionaries must
      // not drop it: this branch used to fall through to `timed_out`, so a fifth
      // state would be announced as "BackOffice may already hold it" — a claim
      // about BackOffice nobody had checked. Runtime stays soft on purpose: the
      // backend can ship a state before this bundle does, and a row tooltip is no
      // place to throw.
      const unknown: never = posting.state;
      return `The ${what} is in a state this page does not know (${String(unknown)})${when} — check the trade in BackOffice`;
    }
  }
}

/**
 * Does BackOffice possibly — or definitely — already hold this booking?
 *
 * The single fact that separates "post it again" from "do not": for `rejected` and
 * `failed` nothing was booked and a repost is safe, while `timed_out` may have
 * been booked and `confirmed_unrecorded` certainly was. On the second pair the
 * guard blocks further postings for the trade, so any copy promising an automatic
 * hourly retry is not merely optimistic — it is wrong about what the system is
 * doing, and it sends the reader away to wait for something that will not happen.
 */
export function mayBeBookedAtBackOffice(posting: BoPostingState): boolean {
  return posting.state === 'timed_out' || posting.state === 'confirmed_unrecorded';
}

/**
 * Why Retry must not be offered on this trade — or `null` when it may be (LR-714).
 *
 * A button is a sentence too. On a `rejected` trade whose posting is `timed_out`
 * or `confirmed_unrecorded`, the banner says "Do NOT retry it yet" while the
 * action bar went on offering Retry, and a reader is entitled to conclude the
 * warning is stale. It is not: those are exactly the states where BackOffice may
 * (or does) already hold the booking, the posting guard is up, and a re-POST is
 * refused — so the click costs a detour and teaches the operator to distrust the
 * banner, which is the more expensive loss.
 *
 * A reason string rather than a boolean, in the shape LR-544 established for the
 * disabled "Open now": the surfaces that gate on it also have to SAY why, and one
 * string keeps the gate and the explanation from drifting apart. It reuses
 * `boPostingHeadline`, so the tooltip names the same posting, in the same words,
 * as the row it hangs off — and no new switch over the states is introduced.
 */
export function retryBlockedReason(
  posting: BoPostingState | null | undefined,
): string | null {
  if (!posting || !mayBeBookedAtBackOffice(posting)) return null;
  // The tail says what the click would actually COST, which is not what an
  // earlier draft claimed (LR-714 round 2 [Medium] #1). `POST /retry/` does not
  // consult the posting guard — it checks only that the trade is `rejected` and
  // answers 200 — so nothing refuses the operator at the moment they press it.
  // The refusal happens an hour later, in the cron, and by then the trade has
  // left `rejected`; a guard refusal is deliberately not counted as a BackOffice
  // failure, so it never promotes the trade back. `rejected` is the only status
  // carrying both Retry and Cancel, which makes the click a ONE-WAY exit from the
  // only place the trade can still be cancelled.
  return (
    `${boPostingHeadline(posting)}. Open the trade and use “Reconcile with ` +
    'BackOffice” — Retry would move this trade out of Rejected, where it would ' +
    'lose both Retry and Cancel, and the posting would still be blocked.'
  );
}

/**
 * The row a submit-time gate must judge: the current one, not the snapshot.
 *
 * A confirm dialog holds the row it was opened with, while the book refreshes
 * itself on window focus and on a timer. The dialog in front of Retry asks the
 * operator to go and check BackOffice — leave the window and come back — which is
 * precisely what triggers a refresh, so "the posting changed while the dialog was
 * open" is the ordinary path, not an exotic race.
 *
 * Returns the snapshot unchanged when the row is gone from the current page
 * (filtered out, or on a page not loaded): dropping the guard because the row
 * moved would be the wrong direction, and the snapshot is still a real posting
 * state — merely an older one.
 */
export function guardRowFor<T extends { id: number }>(
  snapshot: T,
  rows: ReadonlyArray<T>,
): T {
  return rows.find((row) => row.id === snapshot.id) ?? snapshot;
}

export interface BoPostingCopy {
  title: string;
  detail: string;
  tone: 'red' | 'amber';
  /**
   * What takes the button's place once the guard is cleared — the operator's
   * receipt for a click whose effect the attempt row itself will not show until
   * BackOffice answers. Produced HERE, next to the copy it has to agree with,
   * rather than from a second `is_blocked` test in the component: that second
   * test is what LR-711 round 2 left behind on `confirmed_unrecorded`, so the
   * panel dropped the button, dropped this line, and went on instructing the
   * operator to press it.
   */
  cleared?: string;
}

/**
 * The long form, for the trade page's own panel: the same four answers with room
 * to say why the remedy is the remedy.
 *
 * Takes the DETAIL shape because two of its distinctions are detail-only —
 * `reason` (why a timed-out posting has no answer) and `is_blocked` (whether the
 * guard has since been cleared, which the append-only attempt row cannot say).
 */
export function boPostingCopy(posting: BoPostingStateDetail): BoPostingCopy {
  const what = postingLabel(posting);
  switch (posting.state) {
    case 'rejected':
      return {
        tone: 'red',
        title: `BackOffice refused the ${what}`,
        detail:
          'Nothing was booked. BackOffice read the posting and would not take it, ' +
          'so posting it again unchanged fails the same way — the fix is in the trade.',
      };
    case 'failed':
      return {
        tone: 'red',
        title: `The ${what} did not reach BackOffice`,
        detail:
          'Nothing was booked. BackOffice gave no usable answer, which is not a ' +
          'verdict on the trade — the posting is retried automatically once ' +
          'BackOffice is reachable again.',
      };
    case 'confirmed_unrecorded': {
      // The one state where "may or may not hold it" would understate what we
      // know: BackOffice DID book it, and our own write was lost, so the two books
      // disagree right now. Reconciling is still the right move — the re-POST is
      // answered 409, which is the proof that settles the row and writes the local
      // side (LR-709 round 2 / LR-710).
      const booked =
        'The posting went through and BackOffice holds it — what was lost is our ' +
        'own record of it, so the trade does not show a booking that exists there. ';
      // Guard already cleared. The first half above stays true — it is what
      // BackOffice told us — but the second half must not go on describing an
      // action the panel no longer offers.
      //
      // What it may promise is narrower here than on `timed_out`, and
      // deliberately so: only a 409 closes this row. A 200 to the re-POST means
      // BackOffice booked an id it had already accepted, i.e. a possible double —
      // that row is left open on purpose and raised at CRITICAL (LR-710 round 5).
      //
      // Those are two different waits that look identical on the row, and until
      // LR-715 this said the same thing about both: "if the panel is still here
      // after the next scheduled run, that answer did not come". True, and
      // useless — it asked the operator to time a cron they cannot see, to learn a
      // fact the backend already knew. `is_contradicted` is that fact, and it is
      // also the only state in which a person may close this row by hand.
      if (posting.is_contradicted) {
        return {
          tone: 'red',
          title: `BackOffice may hold the ${what} twice`,
          detail:
            booked +
            'We sent it again under the same request id, and BackOffice booked it ' +
            'a second time instead of refusing the repeat — so it now appears ' +
            'there twice, and no answer from BackOffice can settle this. Check the ' +
            'trade in BackOffice, cancel the duplicate, and record what you found.',
        };
      }
      if (posting.is_blocked === false) {
        return {
          tone: 'red',
          title: `BackOffice booked the ${what} — our record of it is still missing`,
          detail:
            booked +
            'The guard is cleared, so nothing on this trade is held back any ' +
            'more. What closes this is BackOffice answering that it already has ' +
            'the posting — this panel clears itself when that answer arrives, and ' +
            'says so here if it turns out to have booked the posting twice instead.',
          cleared:
            'Guard cleared — the posting goes out again under the same request ' +
            'id. This panel stays until BackOffice confirms it already holds it.',
        };
      }
      // A LEGACY posting reaches this state too — LR-713's read-back found it in
      // BackOffice — and everything below about reconciling is false for it. There
      // is no request id to re-send under (that is what legacy means), so the sweep
      // refuses to re-POST it (`if not block.dedup`) and no answer will ever arrive
      // to settle the row. Telling that operator to reconcile sends them round a
      // loop that returns the same answer and changes nothing.
      //
      // Two remedies stand here, and the copy names both in the order they should
      // be tried (LR-715 round 2 + LR-706 (g)): read what BackOffice holds and
      // record it, which makes the books agree; or, if the operator has already put
      // BackOffice right by hand, close the row and say what they did.
      //
      // `is_legacy` and `has_request_id` are the same fact from two tickets — see
      // LR-724, which is to collapse them. Both are read so neither payload shape
      // falls through to the sentence about request ids.
      if (posting.is_legacy || posting.has_request_id === false) {
        return {
          tone: 'red',
          title: `BackOffice holds the ${what}, and only a person can close this`,
          detail:
            booked +
            'This posting was sent before request ids existed, so it cannot be ' +
            're-sent — BackOffice would not recognise the repeat and would book it ' +
            'twice — and no answer of its own will ever settle the row. Read what ' +
            'BackOffice holds and record it; or, if you have already put the books ' +
            'right in BackOffice yourself, close this row by hand and say what you ' +
            'saw.',
        };
      }
      return {
        tone: 'red',
        title: `BackOffice booked the ${what}, but this trade never recorded it`,
        detail:
          booked +
          'Reconciling sends the same posting again under the same request ' +
          'id: BackOffice answers that it already has it, and that answer is what ' +
          'writes our side and closes this.',
      };
    }
    case 'timed_out': {
      const because = posting.reason ? REASON_LABELS[posting.reason] : '';
      const sent =
        `The posting was sent and ${because || 'the outcome was never learned'}, ` +
        'so BackOffice may or may not hold it. ';
      // Already reconciled: the guard is gone and the re-post is scheduled, so the
      // "blocked" sentence would now be a lie. The attempt row still says
      // `timed_out` and will until BackOffice actually answers — that is what the
      // log records, and it is not the same thing as the trade being stuck.
      if (posting.is_blocked === false) {
        return {
          tone: 'amber',
          title: `The ${what} is waiting on BackOffice`,
          detail:
            sent +
            'The guard has been cleared: the posting is sent again under the same ' +
            'request id on the next scheduled run, and BackOffice books it only if ' +
            'it does not already hold it. This panel clears itself once BackOffice ' +
            'answers.',
          cleared:
            'Guard cleared — BackOffice is asked again on the next scheduled run. ' +
            'Nothing more to do here.',
        };
      }
      return {
        tone: 'amber',
        title: `The ${what} never confirmed`,
        detail:
          sent +
          'Further postings for this trade are blocked until that is settled — ' +
          'which is deliberate: retrying blind is how a trade gets booked twice.',
      };
    }
    default:
      return unknownStateCopy(posting.state, what);
  }
}

/**
 * The state this bundle has never heard of — both halves of the answer.
 *
 * **Compile time:** the `never` parameter fails to accept a fifth member the
 * moment one joins the union, and it does so in every place that describes these
 * states, so a new state cannot be taught to one surface and forgotten in the
 * others. That is the failure this family of tickets already lived through once —
 * `confirmed_unrecorded` arrived in LR-709 and reached the panel two rounds later.
 *
 * **Runtime:** the backend ships separately from this bundle, so a state can be
 * real before this code knows it. Falling through to `timed_out`'s words — "may
 * or may not hold it", "never confirmed" — would describe an unknown state as the
 * one thing this family of tickets exists to stop getting wrong. Throwing would
 * cost the operator the whole trade page over a paragraph. So: say plainly that we
 * do not know, and send them to the source of truth. No action is offered either
 * way — `RECONCILABLE_STATES` does not contain it.
 */
function unknownStateCopy(state: never, what: string): BoPostingCopy {
  return {
    tone: 'red',
    title: `The ${what} is in a state this page does not know (${String(state)})`,
    detail:
      'This version of the page cannot say what BackOffice did with the posting, ' +
      'and guessing is exactly what must not happen here — check the trade in ' +
      'BackOffice before acting on it.',
  };
}

/** What the confirm dialog says about the posting it is about to reconcile. */
export interface ReconcileDialogCopy {
  /** Follows "Position 42 (AAPL) " — what is wrong with it. */
  lead: string;
  /** What pressing the button does, and why it cannot double-book. */
  effect: string;
}

/**
 * The dialog's words about the same state, kept next to the panel's (LR-711
 * round 4).
 *
 * It lived in `TradeDetailPage` as a ternary on one state, which is unexhaustive
 * by construction: every state that is not `confirmed_unrecorded` got "never
 * confirmed", including states that do not exist yet. The dialog is the last
 * screen before the action — the worst place to describe a trade as the opposite
 * of what it is — so it belongs where the panel's copy lives and under the same
 * compile-time check.
 */
export function reconcileDialogCopy(
  state: BoPostingStateDetail['state'],
): ReconcileDialogCopy {
  switch (state) {
    case 'confirmed_unrecorded':
      return {
        lead: 'has a BackOffice posting that BackOffice booked but this trade never recorded',
        effect:
          'Reconciling sends it again under the same request id: BackOffice ' +
          'answers that it already has it, and that answer is what writes our ' +
          'side — it cannot create a second booking.',
      };
    case 'timed_out':
      return {
        lead: 'has a BackOffice posting that never confirmed',
        effect:
          'Reconciling re-sends it under the same request id, so BackOffice ' +
          'books it only if it has not already — it cannot create a second booking.',
      };
    // The panel offers Reconcile for the two states above only, so these are
    // reachable solely through a stale render or a hand-built URL. They still get
    // words that claim nothing: the dialog must never be the place where a state
    // is described wrongly.
    case 'rejected':
    case 'failed':
      return {
        lead: 'has a BackOffice posting that was never accepted — nothing was booked',
        effect:
          'Reconciling can settle only a posting whose outcome BackOffice still ' +
          'holds; there is nothing here for it to settle.',
      };
    default: {
      // Same compile-time guard as `unknownStateCopy` above, and for the same
      // reason — see its docstring.
      const unknown: never = state;
      return {
        lead: `has a BackOffice posting in a state this page does not know (${String(unknown)})`,
        effect:
          'Check the trade in BackOffice before acting on it — this version of ' +
          'the page cannot say what reconciling would do.',
      };
    }
  }
}

export interface BoReason<P> {
  /** The posting record, when the trade has one. Authoritative whenever present. */
  posting: P | null;
  /** The pre-LR-709 free-text column — non-empty ONLY when `posting` is null. */
  legacy: string;
}

/**
 * Which record speaks for this trade's BackOffice trouble — and, crucially, only
 * one of them ever does.
 *
 * This is the whole of LR-712 in four lines, and it lives here rather than at each
 * of the four call sites (row tooltip, confirm note, trade-page banner, inspector)
 * because "the string is read only when nothing contradicts it" is an invariant,
 * not a local convenience. Four hand-written copies of it is four chances for one
 * of them to drift back to showing both — which is the state TRD-111 was in.
 *
 * Generic over the posting shape so a blotter row (`BoPostingState`) and a detail
 * payload (`BoPostingStateDetail`) both keep their own type through it.
 */
export function resolveBoReason<P extends BoPostingState>(
  source: { bo_posting?: P | null; last_bo_error?: string } | null | undefined,
): BoReason<P> {
  const posting = source?.bo_posting ?? null;
  return { posting, legacy: posting ? '' : (source?.last_bo_error ?? '') };
}

/**
 * The caption over a legacy `last_bo_error` string.
 *
 * Shown ONLY when a trade has no posting attempt to speak for it — one rejected
 * before LR-709 shipped the attempt log, whose backfill could seed `timed_out` and
 * `confirmed_unrecorded` from the durable guard but had no source for `rejected`.
 * For those the column is the only record that survives, and dropping it would
 * leave a rejected trade with no reason at all, which is the invisibility LR-711
 * was about.
 *
 * It is safe to show precisely because it is unopposed: the defect this ticket
 * removes is the column CONTRADICTING a posting state, and here there is none. The
 * caption still says so, because a string that cannot name its own posting should
 * never be read as if it could.
 */
export const LEGACY_BO_ERROR_CAPTION =
  'Recorded before per-posting tracking — this text may describe a different ' +
  'posting than the one that stalled this trade.';
